import { commonAPI } from "@/api/commonAPI.js";

const POPUP_LAYER_CLASS = "common-popup-layer";
const POPUP_HIDE_STORAGE_PREFIX = "commonPopupHidden";
const DEFAULT_WIDTH = 500;
const DEFAULT_HEIGHT = 600;
let latestOpenRequestId = 0;

const POPUP_HIDE_OPTIONS = {
    "01": { label: "", days: 0 },
    "02": { label: "하루 안보기", days: 1 },
    "03": { label: "7일 안보기", days: 7 },
    "04": { label: "다시 안보기", days: null }
};

const getPopupRows = (response) => {
    const data = response?.data;

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.list)) return data.list;
    if (Array.isArray(data?.result)) return data.result;
    if (Array.isArray(data?.resultList)) return data.resultList;
    if (Array.isArray(data?.resultData)) return data.resultData;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.items)) return data.items;

    return data ? [data] : [];
};

const normalizePath = (url) => {
    if (!url) return "";

    try {
        return new URL(url, window.location.origin).pathname;
    } catch (error) {
        return String(url).split("?")[0];
    }
};

const normalizeFullUrl = (url) => {
    if (!url) return "";

    try {
        const parsedUrl = new URL(url, window.location.origin);
        return `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch (error) {
        return String(url);
    }
};

const normalizeCode = (code) => {
    return String(code || "").padStart(2, "0");
};

const getPopupHideOption = (popup) => {
    return POPUP_HIDE_OPTIONS[normalizeCode(popup.popupHiddnTypeCd)] || POPUP_HIDE_OPTIONS["01"];
};

const getPopupStorageKey = (popup) => {
    const popupId = popup.popupId
        || popup.popupNo
        || popup.popupSn
        || popup.popupSeq
        || popup.popupSno
        || [
            popup.popupApplScrenUrl,
            popup.popupTitle,
            popup.expsrBeginDttm,
            popup.expsrEndDttm
        ].filter(Boolean).join("|");

    return `${POPUP_HIDE_STORAGE_PREFIX}:${encodeURIComponent(String(popupId || ""))}`;
};

const getHiddenPopupValue = (popup) => {
    try {
        const storedValue = window.localStorage.getItem(getPopupStorageKey(popup));
        if (!storedValue) return null;

        const parsedValue = JSON.parse(storedValue);
        if (!parsedValue || typeof parsedValue !== "object") return null;

        return parsedValue;
    } catch (error) {
        return null;
    }
};

const isPopupHidden = (popup) => {
    const hideOption = getPopupHideOption(popup);

    if (!hideOption.label) {
        try {
            window.localStorage.removeItem(getPopupStorageKey(popup));
        } catch (error) {
            // Ignore storage errors and show the popup.
        }

        return false;
    }

    const hiddenPopup = getHiddenPopupValue(popup);
    if (!hiddenPopup) return false;

    if (!Object.prototype.hasOwnProperty.call(hiddenPopup, "expiresAt")) return false;
    if (hiddenPopup.expiresAt === null) return true;

    const expiresAt = Number(hiddenPopup.expiresAt);
    if (!Number.isFinite(expiresAt)) return false;

    if (Date.now() <= expiresAt) return true;

    try {
        window.localStorage.removeItem(getPopupStorageKey(popup));
    } catch (error) {
        // Ignore storage errors and show the popup.
    }

    return false;
};

const hidePopupForSelectedPeriod = (popup) => {
    const hideOption = getPopupHideOption(popup);

    if (!hideOption.label) return;

    const expiresAt = hideOption.days
        ? Date.now() + (hideOption.days * 24 * 60 * 60 * 1000)
        : null;

    try {
        window.localStorage.setItem(
            getPopupStorageKey(popup),
            JSON.stringify({
                code: normalizeCode(popup.popupHiddnTypeCd),
                expiresAt
            })
        );
    } catch (error) {
        // Ignore storage errors and close only for the current session.
    }
};

const isSameTargetUrl = (popupUrl, currentUrl) => {
    if (!popupUrl) return false;

    const popupPath = normalizePath(popupUrl);
    const currentPath = normalizePath(currentUrl);
    const popupFullUrl = normalizeFullUrl(popupUrl);
    const currentFullUrl = normalizeFullUrl(currentUrl);

    return popupFullUrl === currentFullUrl || popupPath === currentPath || popupUrl === currentUrl;
};

const isWithinDisplayPeriod = (popup) => {
    const now = Date.now();
    const beginTime = popup.expsrBeginDttm ? new Date(popup.expsrBeginDttm).getTime() : null;
    const endTime = popup.expsrEndDttm ? new Date(popup.expsrEndDttm).getTime() : null;

    if (beginTime && now < beginTime) return false;
    if (endTime && now > endTime) return false;

    return true;
};

const isMobile = () => {
    return window.matchMedia?.("(max-width: 767px)").matches;
};

const shouldDisplayOnDevice = (popup) => {
    if (isMobile()) return popup.mobilExpsrYn !== "N";
    return popup.pcExpsrYn !== "N";
};

const getPopupContent = (popup) => {
    if (isMobile() && popup.mobilPopupCntnt) return popup.mobilPopupCntnt;
    return popup.pcPopupCntnt || "";
};

const getPopupPosition = (popup, width, height) => {
    const code = String(popup.popupPostnAsgnCd || "").padStart(2, "0");
    const maxTop = Math.max(0, window.innerHeight - height);
    const maxLeft = Math.max(0, window.innerWidth - width);
    const centerTop = Math.round(maxTop / 2);
    const centerLeft = Math.round(maxLeft / 2);

    //직접입력
    if (code === "10") {
        return {
            top: Math.min(maxTop, Math.max(0, Number(popup.manulInputPostnYcoord) || 0)),
            left: Math.min(maxLeft, Math.max(0, Number(popup.manulInputPostnXcoord) || 0))
        };
    }

    const positions = {
        "01": { top: 90, left: 20 },                    // 좌측상단
        "02": { top: 90, left: centerLeft },            // 중앙상단
        "03": { top: 90, left: maxLeft-20 },            // 우측상단
        "04": { top: centerTop, left: 20 },             // 좌측중앙
        "05": { top: centerTop, left: centerLeft },     // 중앙
        "06": { top: centerTop, left: maxLeft-20 },     // 우측중단
        "07": { top: maxTop -20, left: 20 },            // 좌측하단
        "08": { top: maxTop -20, left: centerLeft },    // 중앙하단
        "09": { top: maxTop -20, left: maxLeft-20 }     // 우측하단
    };

    return positions[code] || positions["05"];
};

const getPopupSizeAndPosition = (popup) => {
    const maxWidth = Math.max(100, Math.round(window.innerWidth * 0.9));
    const maxHeight = Math.max(100, Math.round(window.innerHeight * 0.9));
    const width = Math.min(maxWidth, Math.max(100, Math.round(Number(popup.popupWdth) || DEFAULT_WIDTH)));
    const height = Math.min(maxHeight, Math.max(100, Math.round(Number(popup.popupHight) || DEFAULT_HEIGHT)));
    const { top, left } = getPopupPosition(popup, width, height);

    return { width, height, top, left };
};

const DANGEROUS_TAGS = new Set(["script", "object", "embed", "base", "link", "meta"]);
const URL_ATTRIBUTES = new Set(["href", "src", "action", "formaction", "poster"]);

const sanitizePopupDocument = (root) => {
    root.querySelectorAll("*").forEach((element) => {
        const tagName = element.tagName.toLowerCase();

        if (DANGEROUS_TAGS.has(tagName)) {
            element.remove();
            return;
        }

        [...element.attributes].forEach((attribute) => {
            const name = attribute.name.toLowerCase();
            const value = attribute.value.trim().toLowerCase();
            const protocolSeparatorIndex = value.indexOf(":");
            const protocol = protocolSeparatorIndex > -1
                ? value.slice(0, protocolSeparatorIndex)
                : "";

            if (name.startsWith("on")) {
                element.removeAttribute(attribute.name);
                return;
            }

            if (name === "srcdoc") {
                element.removeAttribute(attribute.name);
                return;
            }

            if (URL_ATTRIBUTES.has(name) && (protocol === "javascript" || protocol === "data")) {
                element.removeAttribute(attribute.name);
            }
        });
    });
};

const appendPopupContent = (container, content) => {
    const parser = new DOMParser();
    const popupDocument = parser.parseFromString(String(content || ""), "text/html");

    sanitizePopupDocument(popupDocument.body);
    container.replaceChildren(...Array.from(popupDocument.body.childNodes));
};

const ensurePopupStyle = () => {
    let style = document.getElementById("common-popup-layer-style");

    if (!style) {
        style = document.createElement("style");
        style.id = "common-popup-layer-style";
        document.head.appendChild(style);
    }

    style.id = "common-popup-layer-style";
    style.textContent = `
.${POPUP_LAYER_CLASS} {
    position: fixed;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 48px 42px;
    background: #fff;
    border-radius: 12px;
    box-sizing: border-box;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
    text-align: left;
}
.${POPUP_LAYER_CLASS},
.${POPUP_LAYER_CLASS} * {
    box-sizing: border-box;
}
.${POPUP_LAYER_CLASS} .common-popup-header {
    flex-shrink: 0;
    margin-bottom: 10px;
    padding-bottom: 20px;
    font-size: 1.8rem;
    font-weight: 700;
    border-bottom: 1px solid #333;
    position: relative;
}
.${POPUP_LAYER_CLASS} .common-popup-close {
    width: 25px;
    height: 25px;
    font-size: 0;
    position: absolute;
    top: 0;
    right: 0;
    display: inline-block;
}
.${POPUP_LAYER_CLASS} .common-popup-close:before,
.${POPUP_LAYER_CLASS} .common-popup-close:after {
    width: 30px;
    border-top: 1px solid #333;
    content: "";
    position: absolute;
    top: 50%;
    right: -4px;
    display: inline-block;
}
.${POPUP_LAYER_CLASS} .common-popup-close:before {
    transform: rotate(45deg);
}
.${POPUP_LAYER_CLASS} .common-popup-close:after {
    transform: rotate(-45deg);
}
.${POPUP_LAYER_CLASS} .common-popup-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
}
.${POPUP_LAYER_CLASS} .common-popup-footer {
    display: flex;
    justify-content: flex-start;
    flex-shrink: 0;
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid #e5e5e9;
}
.${POPUP_LAYER_CLASS} .common-popup-hide {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #555;
    font-size: 1.4rem;
    line-height: 1.4;
    cursor: pointer;
}
.${POPUP_LAYER_CLASS} .common-popup-hide:before {
    width: 16px;
    height: 16px;
    border: 1px solid #b8b8c2;
    border-radius: 2px;
    background: #fff;
    content: "";
    flex-shrink: 0;
}
.${POPUP_LAYER_CLASS} .common-popup-hide:hover,
.${POPUP_LAYER_CLASS} .common-popup-hide:focus-visible {
    color: #111;
}
.${POPUP_LAYER_CLASS} .common-popup-hide:focus-visible {
    outline: 2px solid #111;
    outline-offset: 3px;
}
.${POPUP_LAYER_CLASS} img,
.${POPUP_LAYER_CLASS} video,
.${POPUP_LAYER_CLASS} iframe {
    max-width: 100%;
}
@media screen and (max-width:767px) {
    .${POPUP_LAYER_CLASS} {
        border-radius: 0;
        padding: 32px 24px;
    }
}
`;
};

export const closeCommonPopups = (invalidateRequests = true) => {
    if (invalidateRequests) latestOpenRequestId += 1;

    document.querySelectorAll(`.${POPUP_LAYER_CLASS}`).forEach((popup) => {
        popup.remove();
    });
};

const openPopupLayer = (popup, index) => {
    const { width, height, top, left } = getPopupSizeAndPosition(popup);
    const hideOption = getPopupHideOption(popup);
    const popupLayer = document.createElement("section");
    const title = popup.popupTitle || "";

    const popupTop = Math.min(Math.max(0, window.innerHeight - height), top + (index * 20));
    const popupLeft = Math.min(Math.max(0, window.innerWidth - width), left + (index * 20));

    popupLayer.className = POPUP_LAYER_CLASS;
    popupLayer.style.width = `${width}px`;
    popupLayer.style.height = `${height}px`;
    popupLayer.style.top = `${popupTop}px`;
    popupLayer.style.left = `${popupLeft}px`;
    popupLayer.style.zIndex = String(1000 + index);
    const header = document.createElement("div");
    header.className = "common-popup-header";
    header.appendChild(document.createTextNode(title));

    const closeButton = document.createElement("a");
    closeButton.href = "#none";
    closeButton.className = "common-popup-close";
    closeButton.setAttribute("aria-label", "close");
    closeButton.textContent = "close";
    header.appendChild(closeButton);

    const content = document.createElement("div");
    content.className = "common-popup-content";

    popupLayer.append(header, content);
    popupLayer.setAttribute("role", "dialog");
    popupLayer.setAttribute("aria-modal", "false");

    appendPopupContent(content, getPopupContent(popup));

    if (hideOption.label) {
        const footer = document.createElement("div");
        footer.className = "common-popup-footer";

        const hideButton = document.createElement("button");
        hideButton.type = "button";
        hideButton.className = "common-popup-hide";
        hideButton.textContent = hideOption.label;
        footer.appendChild(hideButton);
        popupLayer.appendChild(footer);

        hideButton.addEventListener("click", () => {
            hidePopupForSelectedPeriod(popup);
            popupLayer.remove();
        });
    }

    closeButton.addEventListener("click", (event) => {
        event.preventDefault();
        popupLayer.remove();
    });

    document.body.appendChild(popupLayer);
};

const filterPopups = (popups, currentUrl) => {
    return popups.filter((popup) => {
        return (
            isSameTargetUrl(popup.popupApplScrenUrl, currentUrl) &&
            isWithinDisplayPeriod(popup) &&
            shouldDisplayOnDevice(popup) &&
            !isPopupHidden(popup) &&
            getPopupContent(popup)
        );
    });
};

export const openCommonPopups = async (currentUrl) => {
    if (typeof window === "undefined") return [];

    const requestId = latestOpenRequestId + 1;
    latestOpenRequestId = requestId;
    closeCommonPopups(false);

    try {
        const response = await commonAPI.selectPopup({ popupApplScrenUrl: currentUrl });
        if (requestId !== latestOpenRequestId) return [];

        const popups = filterPopups(getPopupRows(response), currentUrl);

        ensurePopupStyle();
        popups.forEach(openPopupLayer);

        return popups;
    } catch (error) {
        // console.error("Common popup load error:", error);
        return [];
    }
};
